/**
 * WINGMAN-RESEARCH-006: CONFIRM-FIRST REPORT COORDINATOR
 * 
 * Implements strict clarification and explicit user confirmation before any
 * research execution, scraping, report synthesis, or allowance consumption occurs.
 * 
 * CORE INVARIANTS:
 * 1. CLARIFY BEFORE GENERATING:
 *    - Spoken/typed vague requests (e.g. "Write a report on Spain") collect missing details with
 *      one or two concise questions ("Should it focus on travel, business, history, or something else?").
 *    - Specific requests summarize directly into an editable brief without unnecessary questions.
 * 2. EXPLICIT CONFIRMATION:
 *    - Presents an editable brief with: topic, purpose/audience, scope, timeframe, approximate length,
 *      source mode ("OFFLINE DEMO — SYNTHETIC SOURCES"), and output formats.
 *    - Waits for "Yes, create it" or the confirmation button.
 *    - Zero research-worker calls, zero scraping, zero report generation, zero allowance consumption before confirmation.
 *    - Confirmation is bound to exact brief version and authenticated user.
 *    - Edits invalidate previous confirmation (increments version).
 *    - Repeated clicks/events create at most ONE job.
 * 3. ALLOWANCE ENFORCEMENT:
 *    - If allowance is unavailable/exhausted, confirmation halts and reports limit reached.
 *    - Ordinary conversational clarification does not guarantee zero AI/voice turn costs,
 *      but report-generation allowance is strictly protected.
 */

import { ReportIntentClassifier } from '../intelligence/ReportIntentClassifier';
import type { SpatialScope } from '../intelligence/domain';
import { useWingmanStore } from '../../store/useWingmanStore';

export interface ReportBrief {
  briefId: string;
  version: number;
  userId: string;
  userAuthId?: string;
  topic: string;
  purposeAudience: string;
  purpose?: string;
  scope: SpatialScope;
  timeframe: string;
  approximateLength: string;
  sourceMode: string;
  outputFormats: string[];
  confirmed: boolean;
  confirmedVersion?: number;
  createdAt: string;
  updatedAt: string;
}

export type ReportLifecycleStatus =
  | 'idle'
  | 'clarifying'
  | 'brief_pending'
  | 'generating'
  | 'ready'
  | 'failed'
  | 'cancelled'
  | 'allowance_exhausted';

export interface ClarificationTurn {
  stage: 'NONE' | 'AWAITING_FOCUS' | 'AWAITING_DETAILS';
  topic: string;
  suggestedScope?: SpatialScope;
  questionsAsked: number;
}

export interface ReportAllowance {
  allowed: boolean;
  remaining: number;
  total: number;
}

export interface ConfirmationResult {
  success: boolean;
  status: ReportLifecycleStatus;
  jobId?: string;
  message?: string;
  error?: string;
}

export class ReportConfirmationCoordinator {
  private activeBrief: ReportBrief | null = null;
  private clarificationTurn: ClarificationTurn = { stage: 'NONE', topic: '', questionsAsked: 0 };
  private activeJobId: string | null = null;
  private allowance: ReportAllowance = { allowed: true, remaining: 1, total: 1 };
  private classifier = new ReportIntentClassifier();
  private abortController: AbortController | null = null;
  private executionCount = 0; // Invariant audit counter for worker calls

  constructor(initialAllowance?: Partial<ReportAllowance>) {
    if (initialAllowance) {
      this.allowance = { ...this.allowance, ...initialAllowance };
    }
  }

  public getActiveBrief(): ReportBrief | null {
    return this.activeBrief;
  }

  public getAllowance(): ReportAllowance {
    return { ...this.allowance };
  }

  public setAllowance(allowance: Partial<ReportAllowance>): void {
    this.allowance = { ...this.allowance, ...allowance };
    if (this.allowance.remaining <= 0) {
      this.allowance.allowed = false;
    }
  }

  public getExecutionCount(): number {
    return this.executionCount;
  }

  public getClarificationState(): ClarificationTurn {
    return { ...this.clarificationTurn };
  }

  /**
   * Evaluates user utterance. Determines whether to ask a concise clarification question
   * or directly construct an editable brief.
   */
  public evaluateUtterance(
    utterance: string,
    optionsOrLocation?: string | { activeContext?: any; userAuthId?: string; activeLocationName?: string },
    legacyUserId: string = 'user_default'
  ): {
    action: 'CLARIFY' | 'BRIEF_PENDING' | 'CONFIRM' | 'CANCEL' | 'NONE';
    type: 'CLARIFY' | 'BRIEF_PENDING' | 'CONFIRM' | 'CANCEL' | 'NONE';
    reply: string;
    message: string;
    brief?: ReportBrief;
  } {
    let activeLocationName: string | undefined;
    let userId: string = legacyUserId;
    if (typeof optionsOrLocation === 'string') {
      activeLocationName = optionsOrLocation;
    } else if (optionsOrLocation && typeof optionsOrLocation === 'object') {
      activeLocationName = optionsOrLocation.activeLocationName || optionsOrLocation.activeContext?.name;
      userId = optionsOrLocation.userAuthId || 'user_default';
    }

    const norm = (utterance || '').trim().toLowerCase();

    // 1. Explicit confirmation intent
    const isExplicitConfirmation =
      norm === 'yes, create it' ||
      norm === 'yes create it' ||
      norm === 'create it' ||
      norm === 'confirm' ||
      norm === 'confirm report' ||
      norm === 'yes, please generate' ||
      norm === 'generate it' ||
      norm === 'yes, proceed' ||
      norm === 'proceed with report' ||
      norm === 'yes, create' ||
      norm === 'yes, please';

    // 2. Explicit cancellation intent
    const isExplicitCancellation =
      norm === 'cancel' ||
      norm === "don't create it" ||
      norm === 'dont create it' ||
      norm === 'stop' ||
      norm === 'discard' ||
      norm === 'cancel report' ||
      norm === 'no, cancel' ||
      norm.includes('cancel report');

    if (this.activeBrief && !this.activeBrief.confirmed) {
      if (isExplicitConfirmation) {
        const reply = `Confirmed. Generating intelligence report for ${this.activeBrief.topic}...`;
        return {
          action: 'CONFIRM',
          type: 'CONFIRM',
          reply,
          message: reply,
          brief: this.activeBrief
        };
      }

      if (isExplicitCancellation) {
        this.cancelCurrentWorkflow();
        const reply = 'Report request cancelled. No report allowance was consumed.';
        return {
          action: 'CANCELLED',
          type: 'CANCELLED',
          reply,
          message: reply
        };
      }

      // If user is in an ongoing clarification turn, let step 3 handle it
      if (this.clarificationTurn.stage === 'NONE') {
        // Unrelated conversation during pending brief: do NOT treat as consent!
        // Return NONE so ordinary conversational / spatial router handles the message.
        return { action: 'NONE', type: 'NONE', reply: '', message: '' };
      }
    }

    if (isExplicitCancellation && this.clarificationTurn.stage !== 'NONE') {
      this.cancelCurrentWorkflow();
      const reply = 'Report request cancelled. No report allowance was consumed.';
      return {
        action: 'CANCELLED',
        type: 'CANCELLED',
        reply,
        message: reply
      };
    }

    // 3. Ongoing clarification: user is answering our focus question
    if (this.clarificationTurn.stage === 'AWAITING_FOCUS') {
      const topic = this.clarificationTurn.topic || activeLocationName || 'Active Location';
      const focusResponse = utterance.trim();
      
      // Determine domain parameters based on clarification answer
      const params = this.extractReportParameters(focusResponse, topic);
      const brief = this.createDraftBrief(
        topic,
        params.purposeAudience,
        userId,
        params.focus,
        params.timeframe,
        params.scope,
        params.approximateLength,
        params.outputFormats
      );
      this.activeBrief = brief;
      this.clarificationTurn = { stage: 'NONE', topic: '', questionsAsked: 1 };

      const reply = `I've prepared a ${params.focus} report brief for ${topic}. Please review the brief in Spatial Command and say "Yes, create it" or click Confirm to proceed.`;
      return {
        action: 'BRIEF_PENDING',
        type: 'BRIEF_PENDING',
        reply,
        message: reply,
        brief
      };
    }

    // 4. New Report Intent Classification
    const classification = this.classifier.classifyIntent(utterance);
    if (!classification.isReportIntent) {
      return { action: 'NONE', type: 'NONE', reply: '', message: '' };
    }

    // Identify topic
    let topic = classification.requestedLocation || activeLocationName;
    const countryMatch = utterance.match(/\b(Spain|Germany|Japan|France|Italy|United Kingdom|United States|Singapore|Canada|Australia|China|Brazil|Berlin|Tokyo|London|Madrid|Paris|Taiwan|Munich|Barcelona|Valencia)\b/i);
    if (countryMatch) {
      topic = countryMatch[1];
    } else if (topic) {
      const cleanMatch = topic.match(/^([^,]+?)(?:\s+(?:focusing|over|with|for|targeting|covering)\b|$)/i);
      if (cleanMatch && cleanMatch[1]) {
        topic = cleanMatch[1].trim();
      }
    }

    if (!topic) {
      // Missing location/topic entirely
      const reply = 'Which location would you like an intelligence report for?';
      return {
        action: 'CLARIFY',
        type: 'CLARIFY',
        reply,
        message: reply
      };
    }

    // Check if initial request is specific or vague
    const isSpecific = this.isSpecificReportRequest(utterance, classification);

    if (!isSpecific) {
      // Vague request (e.g. "Write a report on Spain")
      this.clarificationTurn = {
        stage: 'AWAITING_FOCUS',
        topic,
        suggestedScope: this.classifier.resolveScope(topic),
        questionsAsked: 1
      };

      const question = `Should it focus on travel, business, history, or something else?`;
      return {
        action: 'CLARIFY',
        type: 'CLARIFY',
        reply: question,
        message: question
      };
    }

    // Specific request: extract tailored focus, audience, scope, timeframe without defaulting to 'business'
    const params = this.extractReportParameters(utterance, topic);
    const brief = this.createDraftBrief(
      topic,
      params.purposeAudience,
      userId,
      params.focus,
      params.timeframe,
      params.scope,
      params.approximateLength,
      params.outputFormats
    );
    this.activeBrief = brief;
    this.clarificationTurn = { stage: 'NONE', topic: '', questionsAsked: 0 };

    const reply = `I've prepared a ${params.focus} intelligence brief for ${topic}. Please review the parameters in Spatial Command and confirm to begin research.`;
    return {
      action: 'BRIEF_PENDING',
      type: 'BRIEF_PENDING',
      reply,
      message: reply,
      brief
    };
  }

  /**
   * Helper to check if user utterance already includes specific purpose, domain or timeframe.
   */
  private isSpecificReportRequest(utterance: string, classification: any): boolean {
    const text = utterance.toLowerCase();
    
    // Explicit mode beyond standard
    if (classification.requestedMode && classification.requestedMode !== 'STANDARD') {
      return true;
    }

    // Check for domain/timeframe keywords with regex word boundaries (avoids "port" matching "report"!)
    const specificKeywords = [
      'business', 'travel', 'traveler', 'history', 'historical', 'ancient', 'commercial', 'real estate',
      'logistics', 'investment', 'investor', 'economic', 'technical', 'engineering', 'architecture',
      'q1', 'q2', 'q3', 'q4', '2026', '2027', 'infrastructure', 'relocation', 'career', 'emergency',
      'tourism', 'deep dive', 'runway', 'freight', 'seaport', 'multimodal', 'hub', 'robotics', 'industry',
      'energy', 'renewable', 'students', 'tourists', 'backpackers', 'clean energy', 'solar', 'grid', 'battery',
      'semiconductor', 'supply chain', 'recycling'
    ];

    const matchCount = specificKeywords.filter(k => new RegExp(`\\b${k}\\b`, 'i').test(text)).length;
    return matchCount >= 1 && text.length > 25;
  }

  /**
   * Extracts domain focus, target audience, scope, timeframe, and formats from utterance.
   */
  public extractReportParameters(utterance: string, locationTopic: string): {
    topic: string;
    focus: 'history' | 'travel' | 'technical' | 'investor' | 'business';
    purposeAudience: string;
    scope: SpatialScope;
    timeframe: string;
    approximateLength: string;
    outputFormats: string[];
  } {
    const text = utterance.toLowerCase();

    // 1. Determine Focus Domain
    let focus: 'history' | 'travel' | 'technical' | 'investor' | 'business' = 'business';
    if (/\b(history|historical|ancient|medieval|heritage|century|past)\b/i.test(text)) {
      focus = 'history';
    } else if (/\b(travel|traveler|tourist|tourism|vacation|trip|sightseeing|attractions|guide|destinations)\b/i.test(text)) {
      focus = 'travel';
    } else if (/\b(technical|engineering|architecture|specs|specifications|protocol|infrastructure|battery|grid|cluster|software|hardware|telemetry|rail)\b/i.test(text)) {
      focus = 'technical';
    } else if (/\b(investor|investment|venture|fund|portfolio|seed|due diligence|valuation|roi|m&a|capital)\b/i.test(text)) {
      focus = 'investor';
    } else if (/\b(business|commercial|logistics|freight|market|industry|enterprise|trade)\b/i.test(text)) {
      focus = 'business';
    }

    // 2. Extract or Tailor Audience
    let audience = '';
    const audienceMatch = utterance.match(/\bfor\s+([a-zA-Z0-9\s,\-_]+?)(?:\s+(?:over|covering|targeting|in|with|focusing)\b|$)/i);
    if (audienceMatch && audienceMatch[1] && audienceMatch[1].trim().length > 2) {
      audience = `${focus.toUpperCase()} briefing for ${audienceMatch[1].trim()}`;
    } else {
      switch (focus) {
        case 'history':
          audience = 'Historical analysis for historians, researchers, and general readers';
          break;
        case 'travel':
          audience = 'Travel and tourism briefing for visitors, travelers, and trip planners';
          break;
        case 'technical':
          audience = 'Technical architecture briefing for engineers and infrastructure specialists';
          break;
        case 'investor':
          audience = 'Investment analysis for institutional investors, fund managers, and venture capital';
          break;
        case 'business':
        default:
          audience = 'Executive business briefing for strategic overview and operations';
          break;
      }
    }

    // 3. Extract or Tailor Timeframe
    let timeframe = '';
    const timeframeMatch = utterance.match(/\b(q[1-4]\s*20\d\d|20\d\d|next\s+\d+\s+years?|next\s+spring|next\s+summer|next\s+year|medieval\s+era|ancient\s+times|19th\s+century|20th\s+century|last\s+\d+\s+years?)\b/i);
    if (timeframeMatch) {
      timeframe = timeframeMatch[0].trim();
    } else {
      switch (focus) {
        case 'history':
          timeframe = 'Historical Overview / Multi-era Chronology';
          break;
        case 'travel':
          timeframe = 'Upcoming Season / Current Travel Windows';
          break;
        case 'technical':
          timeframe = 'Current Technical Specifications (2026)';
          break;
        case 'investor':
          timeframe = '3-5 Year Strategic Investment Horizon';
          break;
        case 'business':
        default:
          timeframe = 'Current / 2026 Operations';
          break;
      }
    }

    // 4. Resolve Scope
    let scope: SpatialScope = this.classifier.resolveScope(locationTopic);
    if (/\blocal\b/i.test(text)) scope = 'LOCAL';
    else if (/\bmetro\b/i.test(text)) scope = 'METRO';
    else if (/\bregional\b/i.test(text)) scope = 'REGIONAL';
    else if (/\bnational\b/i.test(text)) scope = 'NATIONAL';
    else if (/\bglobal\b/i.test(text)) scope = 'GLOBAL';

    // 5. Length
    let approximateLength = 'Executive Brief (3-4 pages)';
    if (/\b(1-2\s*pages?|summary|overview|quick\s*brief)\b/i.test(text)) {
      approximateLength = 'Executive Summary (1-2 pages)';
    } else if (/\b(5-8\s*pages?|5\s*pages?|dossier|comprehensive|deep\s*dive|in-depth)\b/i.test(text)) {
      approximateLength = 'Comprehensive Dossier (5-8 pages)';
    }

    // 6. Formats
    const outputFormats = ['Interactive Briefing', 'Word (.docx)', 'Print / PDF'];

    return {
      topic: locationTopic,
      focus,
      purposeAudience: audience,
      scope,
      timeframe,
      approximateLength,
      outputFormats
    };
  }

  /**
   * Constructs initial draft ReportBrief with version 1 and unconfirmed status.
   */
  public createDraftBrief(
    topic: string,
    purpose: string,
    userId: string,
    focus: string = 'business',
    timeframe: string = 'Current / 2026 Operations',
    scope?: SpatialScope,
    approximateLength: string = 'Executive Brief (3-4 pages)',
    outputFormats: string[] = ['Interactive Briefing', 'Word (.docx)', 'Print / PDF']
  ): ReportBrief {
    const resolvedScope = scope || this.classifier.resolveScope(topic);
    const now = new Date().toISOString();

    const brief: ReportBrief = {
      briefId: `brief_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`,
      version: 1,
      userId,
      userAuthId: userId,
      topic,
      purposeAudience: purpose || `${focus.toUpperCase()} briefing for strategic overview`,
      purpose: purpose || `${focus.toUpperCase()} briefing for strategic overview`,
      scope: resolvedScope,
      timeframe,
      approximateLength,
      sourceMode: 'OFFLINE DEMO — SYNTHETIC SOURCES',
      outputFormats,
      confirmed: false,
      createdAt: now,
      updatedAt: now
    };

    this.activeBrief = brief;
    return brief;
  }

  /**
   * Edits the pending brief.
   * INVARIANT: Every edit increments version and strictly invalidates prior confirmation.
   */
  public updateBrief(briefId: string, updates: Partial<ReportBrief>): ReportBrief {
    if (!this.activeBrief || this.activeBrief.briefId !== briefId) {
      throw new Error(`[ReportConfirmationCoordinator] No active brief found matching ${briefId}`);
    }

    const updatedVersion = this.activeBrief.version + 1;
    this.activeBrief = {
      ...this.activeBrief,
      ...updates,
      version: updatedVersion,
      confirmed: false, // Invalidate prior confirmation
      confirmedVersion: undefined,
      updatedAt: new Date().toISOString()
    };

    return this.activeBrief;
  }

  /**
   * Confirms the brief and launches execution.
   */
  public async confirmBrief(
    briefId: string,
    targetVersion: number,
    userId: string,
    reportRunner: (brief: ReportBrief) => Promise<any> = async () => ({}),
    options?: { idempotencyKey?: string }
  ): Promise<ConfirmationResult> {
    const brief = this.activeBrief;
    if (!brief || brief.briefId !== briefId) {
      return { success: false, status: 'failed', error: 'No matching active brief to confirm.' };
    }

    // 1. User binding check
    const briefUserId = brief.userId || (brief as any).userAuthId;
    if (briefUserId && briefUserId !== userId) {
      return {
        success: false,
        status: 'failed',
        error: `User mismatch: brief belongs to ${briefUserId}, confirmation attempted by ${userId}.`
      };
    }

    // 2. Exact version binding check (edits invalidate older versions)
    if (brief.version !== targetVersion) {
      return {
        success: false,
        status: 'brief_pending',
        error: `Brief version mismatch: active brief is v${brief.version}, confirmation attempted for v${targetVersion}. Please review updated brief.`
      };
    }

    // 3. Deduplication / Idempotency: Repeated clicks/events must create AT MOST ONE job
    if (brief.confirmed || this.activeJobId) {
      return {
        success: false,
        status: 'in_progress',
        jobId: this.activeJobId || brief.briefId,
        message: 'Job already in progress. Ignoring duplicate submission.'
      };
    }

    // 4. Allowance Check: verify user has available report quota
    const storeAllowance = useWingmanStore.getState().reportAllowance;
    const remaining = storeAllowance ? storeAllowance.remaining : this.allowance.remaining;
    if (remaining <= 0) {
      return {
        success: false,
        status: 'allowance_exhausted',
        error: 'Report allowance exhausted. 0 of 1 reports remaining for your tier.'
      };
    }

    // 5. Commit confirmation state
    brief.confirmed = true;
    brief.confirmedVersion = targetVersion;
    const jobId = options?.idempotencyKey ? `job_${options.idempotencyKey}` : `job_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`;
    this.activeJobId = jobId;

    // Deduct report allowance on confirmed dispatch
    this.allowance.remaining = Math.max(0, this.allowance.remaining - 1);
    if (this.allowance.remaining === 0) {
      this.allowance.allowed = false;
    }
    if (storeAllowance) {
      useWingmanStore.getState().setReportAllowance({
        ...storeAllowance,
        remaining: Math.max(0, storeAllowance.remaining - 1)
      });
    }

    // Initialize abort controller for cancellation support
    this.abortController = new AbortController();

    try {
      this.executionCount++; // Record actual worker invocation
      const reportResult = await reportRunner(brief);
      this.activeJobId = null;
      return {
        success: true,
        status: 'ready',
        jobId,
        message: `Intelligence report compiled for ${brief.topic}.`
      };
    } catch (err: any) {
      this.activeJobId = null;
      
      // Refund allowance if runner failed or was cancelled pre-completion
      this.allowance.remaining = Math.min(this.allowance.total, this.allowance.remaining + 1);
      if (this.allowance.remaining > 0) {
        this.allowance.allowed = true;
      }
      if (storeAllowance) {
        useWingmanStore.getState().setReportAllowance({
          ...storeAllowance,
          remaining: Math.min(storeAllowance.total, storeAllowance.remaining + 1)
        });
      }

      if (err?.name === 'AbortError' || this.abortController?.signal?.aborted) {
        return {
          success: false,
          status: 'cancelled',
          message: 'Report generation cancelled by user.'
        };
      }
      return {
        success: false,
        status: 'failed',
        error: err?.message || 'Report generation failed.'
      };
    }
  }

  /**
   * Cancels active report generation or pending brief.
   * Consumes zero report allowance.
   */
  public cancelCurrentWorkflow(): { status: ReportLifecycleStatus; message: string } {
    if (this.abortController && !this.abortController.signal.aborted) {
      this.abortController.abort();
    }
    this.activeJobId = null;
    this.clarificationTurn = { stage: 'NONE', topic: '', questionsAsked: 0 };
    this.activeBrief = null;
    return {
      status: 'cancelled',
      message: 'Report request cancelled. Zero allowance consumed.'
    };
  }

  public isReportConversationActive(): boolean {
    return this.clarificationTurn.stage !== 'NONE' || (this.activeBrief !== null && !this.activeBrief.confirmed);
  }

  public getStage(): 'NONE' | 'AWAITING_FOCUS' | 'AWAITING_DETAILS' | 'BRIEF_PENDING' | 'AWAITING_CLARIFICATION' {
    if (this.clarificationTurn.stage === 'AWAITING_FOCUS' || this.clarificationTurn.stage === 'AWAITING_DETAILS') {
      return 'AWAITING_CLARIFICATION';
    }
    if (this.activeBrief && !this.activeBrief.confirmed) {
      return 'BRIEF_PENDING';
    }
    return 'NONE';
  }

  public resetState(): void {
    this.reset();
  }

  public setPendingBriefForTesting(brief: ReportBrief): void {
    this.activeBrief = brief;
  }

  public reset(): void {
    this.activeBrief = null;
    this.clarificationTurn = { stage: 'NONE', topic: '', questionsAsked: 0 };
    this.activeJobId = null;
    this.executionCount = 0;
    this.allowance = { allowed: true, remaining: 10, total: 10 };
  }
}

export const reportConfirmationCoordinator = new ReportConfirmationCoordinator();
